import "./calendarPicker.scss";

import { isNil } from "lodash";
import PropTypes from "prop-types";
import React from "react";

import {
  DATE_CHANGE_FUNCTIONS,
  DATE_FIELD_EARLIEST_END,
  DATE_FIELD_END,
  DATE_FIELD_LATEST_START,
  DATE_FIELD_START,
  DATE_FIELDS,
  getDateFieldTitle
} from "../../constants/dateFields";
import { DEFAULT_DISPLAY_LABELS } from "../../constants/styles/typeLabels";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { adjustEndDateWhenSave } from "../../modules/definition/calendar/dateFunctions/dateWithString";
import { getIs2400 } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { Precision, PropTypeShapes } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import { DateRecord } from "../../types/immutableRecords/model/data/DateRecord";
import { TimespanRecord } from "../../types/immutableRecords/model/data/TimespanRecord";
import preprocessorQuery from "../../utils/preprocessorQuery";
import FormDatePicker from "../form/formDatePicker/FormDatePicker";
import Icon from "../interface/Icon";
import { withPopoverRepositionContext } from "../popover/PopoverRepositionContext";
import CalendarPickerPrecisionSelector from "./CalendarPickerPrecisionSelector";
import CalendarPickerRow from "./CalendarPickerRow";
import CalendarPickerSelector from "./CalendarPickerSelector";
import { getPrecisionOfDate } from "./utils";

const precisionOptionWeek = { label: "Week", value: Precision.Week };

const precisionDefaultOptions = [
  {
    label: "Year",
    value: Precision.Year
  },
  {
    label: "Month",
    value: Precision.Month
  },
  {
    label: "Day",
    value: Precision.Day
  },
  {
    label: "Time",
    value: Precision.Second
  }
];

const getPrecisionOptions = calendar => {
  switch (calendar.calendarType) {
    case calendarTypes.CALENDAR_TYPE_DATE_TIME:
      return precisionDefaultOptions;
    case calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME: {
      const options = precisionDefaultOptions.filter(opt => opt.value >= Precision.Week);
      options.unshift(precisionOptionWeek);
      return options;
    }
    case calendarTypes.CALENDAR_TYPE_DAY_TIME:
      return precisionDefaultOptions.filter(opt => opt.value >= Precision.Day);
    case calendarTypes.CALENDAR_TYPE_HOUR_TIME:
      return precisionDefaultOptions.filter(opt => opt.value >= Precision.Second);
    default:
      return precisionDefaultOptions;
  }
};

const isIos = preprocessorQuery.isIos();

const getIsDateFieldExpanded = (useProps, dateKey) =>
  useProps.selectedDateField === dateKey || isDateValid(useProps[dateKey]);

const getDateFieldValues = useProps => {
  return DATE_FIELDS.reduce((obj, key) => {
    obj[key] = useProps[key];
    return obj;
  }, {});
};

const getExpandIcon = (allowUncertainDates, field, isEndDate, isExpanded, isUncertainDateValid, onClick) => {
  if (allowUncertainDates) {
    let icon = "";
    let allowClick = true;
    if (isEndDate) {
      if (field === DATE_FIELD_END) {
        icon = isExpanded ? (isUncertainDateValid ? "" : "upArrow") : "downArrow";
        allowClick = !isExpanded || !isUncertainDateValid;
      } else if (field === DATE_FIELD_EARLIEST_END) {
        icon = isExpanded && isUncertainDateValid ? "closeWindow" : "";
        allowClick = isExpanded && isUncertainDateValid;
      }
    } else {
      if (field === DATE_FIELD_START) {
        icon = isExpanded ? "" : "downArrow";
        allowClick = !isExpanded;
      } else if (field === DATE_FIELD_LATEST_START) {
        icon = isExpanded ? (isUncertainDateValid ? "closeWindow" : "upArrow") : "";
        allowClick = isExpanded;
      }
    }
    const onClickInner = (e, ...props) => {
      e.stopPropagation();
      if (allowClick) {
        onClick(...props);
      }
    };
    return allowUncertainDates ? (
      <Icon icon={icon} className="collapseArrow" color="iconMono" onClick={onClickInner} />
    ) : null;
  }
  return null;
};

const getFieldTitle = (field, isDual, allowUncertainDates, isStartExpanded, isEndExpanded, startLabel, endLabel) => {
  let title = getDateFieldTitle(field, startLabel, endLabel);
  if (!isDual && !allowUncertainDates) {
    title = "Date";
  } else {
    if (isStartExpanded && field === DATE_FIELD_START) {
      title = `Earliest ${startLabel}`;
    } else if (isEndExpanded && field === DATE_FIELD_END) {
      title = `Latest ${endLabel}`;
    }
  }
  return title;
};

class CalendarPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = Object.assign(
      {
        selectedDateField: props.selectedDateField,
        precision: getPrecisionOfDate(props[props.selectedDateField], props.calendar),
        isStartExpanded: getIsDateFieldExpanded(props, "latestStartDate"),
        isEndExpanded: getIsDateFieldExpanded(props, "earliestEndDate")
      },
      getDateFieldValues(props)
    );
    this.setPrecision = this.setPrecision.bind(this);
    this.focusDateField = this.focusDateField.bind(this);
    this.onStartDateChange = this.onStartDateChange.bind(this);
    this.onEndDateChange = this.onEndDateChange.bind(this);
    this.toggleStartExpanded = this.toggleStartExpanded.bind(this);
    this.toggleEndExpanded = this.toggleEndExpanded.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    const changedPrecision = getPrecisionOfDate(props[props.selectedDateField], props.calendar);
    const selectedDateField = props.selectedDateField;
    if (props[selectedDateField] !== state[selectedDateField]) {
      return Object.assign(
        {
          selectedDateField,
          precision: changedPrecision,
          isStartExpanded: state.isStartExpanded,
          isEndExpanded: state.isEndExpanded
        },
        getDateFieldValues(props)
      );
    }
    return null;
  }

  componentDidUpdate() {
    if (this.props.repositionPopover) {
      this.props.repositionPopover();
    }
  }

  setPrecision(newPrecision) {
    const changeEndDates = this.state.selectedDateField.toLowerCase().includes("end");

    this.props.onPrecisionChange(newPrecision, changeEndDates);
    if (this.state.precision !== newPrecision) {
      this.setState({ precision: newPrecision });
    }
  }

  focusDateField(field) {
    let fieldForPrecision = field;
    if (!isDateValid(this.state[field])) {
      switch (field) {
        case DATE_FIELD_START:
          fieldForPrecision = DATE_FIELD_LATEST_START;
          break;
        case DATE_FIELD_LATEST_START:
          fieldForPrecision = DATE_FIELD_START;
          break;
        case DATE_FIELD_EARLIEST_END:
          fieldForPrecision = DATE_FIELD_END;
          break;
        case DATE_FIELD_END:
          fieldForPrecision = DATE_FIELD_EARLIEST_END;
          break;
      }
    }
    this.setState(
      {
        selectedDateField: field,
        precision: getPrecisionOfDate(this.state[fieldForPrecision], this.props.calendar, this.state.precision)
      },
      () => {
        if (this.props.onFocusField) {
          this.props.onFocusField(field);
        }
      }
    );
  }

  onStartDateChange(date) {
    const { selectedDateField } = this.state;
    const onChange = this.props[DATE_CHANGE_FUNCTIONS[selectedDateField]];
    onChange(date);
  }

  onEndDateChange(date) {
    if (date) {
      const { startDate, calendar } = this.props;
      const { selectedDateField } = this.state;
      const isNormalEndDate = selectedDateField === DATE_FIELD_END;
      const duration = this.props[isNormalEndDate ? "duration" : "durationToEarliestEnd"];
      const onChange = this.props[DATE_CHANGE_FUNCTIONS[selectedDateField]];
      let adjustedEndDate = adjustEndDateWhenSave(calendar, date, startDate, duration);
      if (adjustedEndDate.precision > Precision.Day && getIs2400(startDate, adjustedEndDate, calendar.timeFormat)) {
        adjustedEndDate = dateByAddingSpan(calendar, adjustedEndDate, TimespanRecord({ days: 1 }));
      }
      onChange(adjustedEndDate);
    }
  }

  toggleStartExpanded() {
    const { isStartExpanded } = this.state;
    const { onLatestStartChange } = this.props;
    if (isStartExpanded) {
      const emptyDate = DateRecord();
      onLatestStartChange(emptyDate);
      if (this.state.selectedDateField === DATE_FIELD_LATEST_START) {
        this.setState({ selectedDateField: DATE_FIELD_START });
      }
    }
    this.setState({ isStartExpanded: !isStartExpanded });
  }

  toggleEndExpanded() {
    const { isEndExpanded } = this.state;
    const { onEarliestEndChange } = this.props;
    if (isEndExpanded) {
      const emptyDate = DateRecord();
      onEarliestEndChange(emptyDate);
      if (this.state.selectedDateField === DATE_FIELD_EARLIEST_END) {
        this.setState({ selectedDateField: DATE_FIELD_END });
      }
    }
    this.setState({ isEndExpanded: !isEndExpanded });
  }

  getCalendarPickerRow(field) {
    const {
      isDual,
      calendar,
      allowUncertainDates,
      startDate,
      startLabel,
      endLabel,
      ongoing,
      isDateLocked
    } = this.props;
    const { isStartExpanded, isEndExpanded, selectedDateField, latestStartDate, earliestEndDate } = this.state;

    const title = getFieldTitle(
      field,
      isDual,
      allowUncertainDates,
      isStartExpanded,
      isEndExpanded,
      startLabel,
      endLabel
    );
    const date = this.props[field];
    const onChange = this.props[DATE_CHANGE_FUNCTIONS[field]];
    const isEndDate = field.toLowerCase().includes("end");
    const disabled = isDateLocked || ((field === DATE_FIELD_EARLIEST_END || field === DATE_FIELD_END) && ongoing);

    const autoFocus = !isIos && field === this.state.selectedDateField;

    return (
      <CalendarPickerRow
        onClick={() => this.focusDateField(field)}
        selected={selectedDateField === field}
        title={title}
        isSelectable={isDual ? true : false}
      >
        <FormDatePicker
          isDual={false}
          allowUncertainDates={false}
          selectedDateField={isEndDate ? DATE_FIELD_END : DATE_FIELD_START}
          startDate={isEndDate ? startDate : date}
          endDate={isEndDate ? date : null}
          isEndDate={isEndDate}
          onStartDateChange={isEndDate ? null : onChange}
          onEndDateChange={isEndDate ? onChange : null}
          calendar={calendar}
          hasCalendarPicker={false}
          withinCalendarPicker={true}
          onFocus={() => this.focusDateField(field)}
          startLabel={startLabel}
          endLabel={endLabel}
          ongoing={isEndDate ? ongoing : false}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        {getExpandIcon(
          allowUncertainDates,
          field,
          isEndDate,
          isEndDate ? isEndExpanded : isStartExpanded,
          isEndDate ? earliestEndDate : latestStartDate,
          isEndDate ? this.toggleEndExpanded : this.toggleStartExpanded
        )}
      </CalendarPickerRow>
    );
  }

  render() {
    const { isDual, startDate, calendar, allowUncertainDates, ongoing, isDateLocked } = this.props;
    const { selectedDateField, precision, isStartExpanded, isEndExpanded } = this.state;

    const calendarPickerDate = this.props[selectedDateField];

    const showEndDatesOnly = this.props.selectedDateField.toLowerCase().includes("end") && !isDual;
    const showStartDatesOnly = this.props.selectedDateField.toLowerCase().includes("start") && !isDual;

    const isEndDate = selectedDateField.toLowerCase().includes("end");

    const hideCalendarSelector =
      isDateLocked ||
      ((this.state.selectedDateField === DATE_FIELD_EARLIEST_END || this.state.selectedDateField === DATE_FIELD_END) &&
        ongoing);

    return (
      <div className="calendarPickerComponent">
        {this.props.isFixedPrecision || calendar.calendarType !== calendarTypes.CALENDAR_TYPE_DATE_TIME ? null : (
          <CalendarPickerRow title={"Precision"}>
            <CalendarPickerPrecisionSelector
              value={precision}
              options={getPrecisionOptions(calendar)}
              onChange={this.setPrecision}
              disabled={isDateLocked}
            />
          </CalendarPickerRow>
        )}
        {!showEndDatesOnly ? this.getCalendarPickerRow(DATE_FIELD_START) : null}
        {!showEndDatesOnly && allowUncertainDates && isStartExpanded
          ? this.getCalendarPickerRow(DATE_FIELD_LATEST_START)
          : null}
        {!showStartDatesOnly && allowUncertainDates && isEndExpanded
          ? this.getCalendarPickerRow(DATE_FIELD_EARLIEST_END)
          : null}
        {!showStartDatesOnly ? this.getCalendarPickerRow(DATE_FIELD_END) : null}

        {!hideCalendarSelector ? (
          <CalendarPickerSelector
            calendar={calendar}
            date={
              isDateValid(calendarPickerDate) && !isNil(calendarPickerDate.precision)
                ? calendarPickerDate
                : DateRecord({ precision: precision })
            }
            onChange={isEndDate ? this.onEndDateChange : this.onStartDateChange}
            isEndDate={isEndDate}
            startDate={startDate}
          />
        ) : null}
      </div>
    );
  }
}

CalendarPicker.defaultProps = {
  isDual: false,
  allowUncertainDates: true,
  startLabel: DEFAULT_DISPLAY_LABELS.START_DATE,
  endLabel: DEFAULT_DISPLAY_LABELS.END_DATE
};

CalendarPicker.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  startDate: PropTypeShapes.dateRelated,
  latestStartDate: PropTypeShapes.dateRelated,
  earliestEndDate: PropTypeShapes.dateRelated,
  endDate: PropTypeShapes.dateRelated,
  allowUncertainDates: PropTypes.bool.isRequired,
  isDual: PropTypes.bool.isRequired,
  selectedDateField: PropTypes.string.isRequired,
  onStartDateChange: PropTypes.func,
  onLatestStartChange: PropTypes.func,
  onEarliestEndChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onPrecisionChange: PropTypes.func,
  dateForNow: PropTypeShapes.date,
  durationToEarliestEnd: PropTypeShapes.timespan,
  duration: PropTypeShapes.timespan,
  onFocusField: PropTypes.func,
  startLabel: PropTypes.string.isRequired,
  endLabel: PropTypes.string.isRequired,
  ongoing: PropTypes.bool,
  isDateLocked: PropTypes.bool,
  repositionPopover: PropTypes.func,
  // Precision cannot change
  isFixedPrecision: PropTypes.bool
};

export default withPopoverRepositionContext(CalendarPicker);
